import { useQuery } from '@tanstack/react-query';
import type { CallSource, CasePatch, ReferenceItem } from '../api/client.js';
import { api } from '../api/client.js';

/**
 * The OCC_STAT reporting fields.
 *
 * These are the columns the monthly board report is built from — calls by
 * service type, percentage transported, serviced by us versus a provider,
 * revenue by project number. Every one is a GROUP BY, which is why they are real
 * columns on the case and not jsonb.
 *
 * WHY THEY ARE ONE BLOCK AND NOT SPRINKLED THROUGH THE CARD. OCC_STAT groups its
 * columns by the ROLE that owns them, and this set belongs to the dispatcher but
 * is not what they fill in first. The address, the complaint and the callback
 * number get an ambulance moving; these get the month reported. Putting them
 * above the clinical questions would slow down every call to serve a report
 * nobody reads until the 1st.
 *
 * Most default themselves — the call source fills the project number, and the
 * DID already implies the source on a lot of calls.
 */

interface ReferenceLists {
  lists: Record<string, ReferenceItem[]>;
}

const SELECT =
  'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 ' +
  'focus:border-sky-500 focus:outline-none';
const LABEL = 'mb-1 block text-xs font-medium text-slate-300';

function Select({
  label,
  value,
  items,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  items: ReferenceItem[];
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <select className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {items.map((i) => (
          <option key={i.key} value={i.key}>
            {i.label}
          </option>
        ))}
      </select>
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

export function OccFields({
  spine,
  onPatch,
}: {
  spine: Record<string, unknown>;
  onPatch: (patch: CasePatch) => void;
}) {
  // One request for every dropdown on the card. Six requests to paint one form,
  // on a page opened at the start of every emergency call, is a needless
  // multiplier on the screen where latency matters most.
  const { data: refs } = useQuery({
    queryKey: ['reference-lists'],
    queryFn: () => api.get<ReferenceLists>('/reference-lists'),
    // Reference data changes when an admin edits it, which is approximately
    // never during a shift.
    staleTime: 30 * 60 * 1000,
  });

  const { data: sources } = useQuery({
    queryKey: ['call-sources'],
    queryFn: () => api.get<{ sources: CallSource[] }>('/call-sources'),
    staleTime: 30 * 60 * 1000,
  });

  const lists = refs?.lists ?? {};
  const str = (k: string) => (typeof spine[k] === 'string' ? spine[k] : '');

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Reporting
      </h4>

      <div className="space-y-3">
        <div>
          <label className={LABEL}>Call source</label>
          <select
            className={SELECT}
            value={str('call_source_id')}
            onChange={(e) => {
              const src = sources?.sources.find((s) => s.id === e.target.value);
              // Source and project number in ONE patch. The project number is
              // derived from the source, so two requests would leave a window
              // where the case shows a new source against the old project — and
              // that is what a month-end report would pick up.
              onPatch({
                callSourceId: e.target.value,
                ...(src?.project_number ? { projectNumber: src.project_number } : {}),
              });
            }}
          >
            <option value="">—</option>
            {sources?.sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
                {s.project_number ? ` · ${s.project_number}` : ''}
              </option>
            ))}
          </select>
          {str('project_number') && (
            <p className="mt-1 text-[11px] text-slate-500">
              Project <span className="text-slate-300">{str('project_number')}</span> · filled in
              from the call source
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Nature of incident"
            value={str('nature_of_incident')}
            items={lists['nature_of_incident'] ?? []}
            onChange={(v) => onPatch({ natureOfIncident: v })}
          />
          <Select
            label="Service type"
            value={str('service_type')}
            items={lists['service_type'] ?? []}
            onChange={(v) => onPatch({ serviceType: v })}
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Select
            label="Serviced by"
            value={str('serviced_by')}
            items={lists['serviced_by'] ?? []}
            onChange={(v) => onPatch({ servicedBy: v })}
            hint="Us, or the provider the job went to."
          />
          <Select
            label="Reason for provider activation"
            value={str('sp_activation_reason')}
            items={lists['sp_activation_reason'] ?? []}
            onChange={(v) => onPatch({ spActivationReason: v })}
            hint="Only when somebody else attended."
          />
        </div>

        <div>
          <label className={LABEL}>Billable by Medi Response?</label>
          <div className="flex flex-wrap gap-1">
            {(
              [
                { v: 'yes', label: 'Yes' },
                { v: 'no', label: 'No' },
                // Not a "no". A service given to someone who cannot pay is
                // reported on deliberately and separately — collapsing it into
                // "no" would erase what the company chose to do.
                { v: 'indigent', label: 'Indigent' },
              ] as const
            ).map((o) => {
              const active = str('billable') === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => onPatch({ billable: active ? '' : o.v })}
                  className={
                    'rounded px-2.5 py-1 text-xs font-medium transition-colors ' +
                    (active
                      ? 'bg-sky-600 text-white'
                      : 'bg-slate-800 text-slate-300 hover:bg-slate-700')
                  }
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
